import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useCallback, useState, MouseEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  NodeChange,
  NodeDimensionChange,
  Node,
  Background,
  BackgroundVariant,
  Controls,
  addEdge,
  Connection,
  Edge,
  ConnectionLineType,
} from 'reactflow';

import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import {
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ComplexPolymerAttachmentStyle,
  ComplexPolymerConnection,
  ComplexPolymerConnectionType,
  ComplexPolymerType,
  ConjugateGraphQL,
  ConnectionGraphQL,
  connectionHash,
  ResolvedAttachmentGraphQL,
  resolvedAttachmentHash,
  ResolvedAttachmentInput,
  SimplePolymerGraphQL,
  StructureGraphGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  generatePolymerStructureGraphNodes,
  generatePolymerStructureGraphEdges,
  layoutPolymerStructureGraph,
} from 'modbio/complex-polymers/GeneratePolymerStructureGraphUtil';
import {
  DEFAULT_CONJUGATE_NODE_HEIGHT,
  DEFAULT_CONJUGATE_NODE_WIDTH,
  EdgeType,
  NodeType,
  NucleotideStructureGraphNodeData,
  StructureGraphNode,
} from 'modbio/complex-polymers/PolymerStructureGraphConstants';
import {updateComplexPolymerEditingState} from 'modbio/redux/ComplexPolymerReduxActions';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {capitalize} from 'util/NameUtil';

import * as ComplexPolymerActions from '../ComplexPolymerActions';
import {
  generateEntityAssociationsFromStructure,
  getConnectionInputs,
  getResolvedAttachmentInput,
  isPolymerBackboneStrand,
} from '../ComplexPolymerUtil';
import ComplexPolymerAttachConjugateModal, {
  ComplexPolymerAttachConjugateModalProps,
} from '../tabs/ComplexPolymerAttachConjugateModal';
import {
  getComplexPolymerConnectionFromConjugateConnection,
  isCompleteConjugateConnection,
  getResolvedAttachmentsInputsForSubmit,
} from '../tabs/ComplexPolymerAttachConjugateModalUtil';
import {getExistingConnectionInputs} from '../tabs/ComplexPolymerComponentsSummary';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
  makeConjugateConnections,
} from '../tabs/ComplexPolymerComponentsSummaryUtil';
import {
  ComplexPolymerDragAndDropRemoveAttachmentWarningModalProps,
  ComplexPolymerDragAndDropRemoveAttachmentWarningModal,
} from '../tabs/ResolvedAttachmentSummary';
import {
  NucleotideAxis,
  ModifiedNucleotidesLegend,
  EditToggleButton,
  WarningBanner,
} from './ComplexPolymerDragAndDropCanvasElements';
import {ComplexPolymerDragAndDropCanvasContext} from './ComplexPolymerDragAndDropContext';
import {CustomNucleotideNode, CustomConjugateChipNode} from './ComplexPolymerDragAndDropCustomNodes';
import {
  doesConnectionMatchConjugateConnection,
  doesEdgeMatchComplexPolymerConnection,
  doesEdgeMatchConjugateConnection,
  getNodeById,
  getNucleotideAxisDatas,
  transformStructureGraphEdgeForCanvas,
  transformStructureGraphNodeForCanvas,
} from './ComplexPolymerDragAndDropUtil';

function handleNodeDimensionChange(nodes: Array<Node>, change: NodeDimensionChange): Array<Node> {
  return nodes.map((node) => {
    if (node.id === change.id) {
      return {
        ...node,
        data: {
          ...node.data,
          width: change.dimensions?.width ?? DEFAULT_CONJUGATE_NODE_WIDTH,
          height: change.dimensions?.height ?? DEFAULT_CONJUGATE_NODE_HEIGHT,
        },
      };
    }

    return node;
  });
}

function addConnection(
  connection: Connection,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  conjugateConnectionMap: ConjugateConnectionMap,
  setConjugateConnectionMap: (map: ConjugateConnectionMap) => void,
  canvasProps: ComplexPolymerDragAndDropCanvasProps,
  nodes: Array<StructureGraphNode>,
  edges: Array<Edge>,
  setEdges: (edges: Array<Edge>) => void,
  updateConnectionState: (
    conjugateConnection: ConjugateConnection,
    attachmentMolfile: string | null,
    isEditMode: boolean
  ) => void
): void {
  const {complexPolymerType, complexPolymerId, simplePolymers} = canvasProps;

  const sourceNode = getNodeById(connection.source!, nodes);
  const targetNode = getNodeById(connection.target!, nodes);
  const sourcePolymerRef = sourceNode.data.polymerRef;
  const targetPolymerRef = targetNode.data.polymerRef;

  if (sourcePolymerRef === targetPolymerRef) {
    notifications.warn('Conjugate structure must involve more than one component polymer');
    return;
  }

  const conjugateConnectionMatches = Object.entries(conjugateConnectionMap).filter(
    ([_, conjugateConnection]) =>
      (conjugateConnection.helmReference === sourcePolymerRef ||
        conjugateConnection.helmReference === targetPolymerRef) &&
      conjugateConnection.linkedEntityHelmReference == null
  );

  if (conjugateConnectionMatches.length > 1) {
    notifications.warn('Conjugate structure must involve a backbone oligo polymer');
    return;
  } else if (conjugateConnectionMatches.length === 0) {
    notifications.warn('Conjugate structure not currently supported');
    return;
  }

  const [conjugateConnectionKey, conjugateConnection] = conjugateConnectionMatches[0];

  let linkedEntityResidueIndex;
  if (
    conjugateConnection.helmReference === sourcePolymerRef &&
    isPolymerBackboneStrand(complexPolymerType, targetPolymerRef)
  ) {
    linkedEntityResidueIndex = (targetNode.data as NucleotideStructureGraphNodeData).residueIndex;
  } else if (
    conjugateConnection.helmReference === targetPolymerRef &&
    isPolymerBackboneStrand(complexPolymerType, sourcePolymerRef)
  ) {
    linkedEntityResidueIndex = (sourceNode.data as NucleotideStructureGraphNodeData).residueIndex;
  }

  // We need an updated map for the attachment modal, but conjugateConnectionMap is stored as state, so we
  // cannot directly edit it here. Instead, we make an editable copy.
  const editableConjugateConnectionMap = deepCopy(conjugateConnectionMap);
  editableConjugateConnectionMap[conjugateConnectionKey] = {
    ...conjugateConnection,
    linkedEntityHelmReference:
      conjugateConnection.helmReference === sourcePolymerRef ? targetPolymerRef : sourcePolymerRef,
    linkedEntityResidueIndex,
  };

  legacyRunModal<ComplexPolymerAttachConjugateModalProps>(ComplexPolymerAttachConjugateModal, {
    isEditMode: false,
    complexPolymerType,
    complexPolymerId,
    startingConjugateRef: conjugateConnection.helmReference,
    simplePolymers,
    resolvedAttachments: resolvedAttachmentInputs,
    conjugateConnectionMap: editableConjugateConnectionMap,
    onRequestClose: () => null,
    onAddConnection: (newConjugateConnection: ConjugateConnection, attachmentMolfile: string | null) => {
      editableConjugateConnectionMap[newConjugateConnection.helmReference] = newConjugateConnection;
      setConjugateConnectionMap(editableConjugateConnectionMap);
      updateConnectionState(newConjugateConnection, attachmentMolfile, false);
      setEdges([...addEdge({...connection, type: EdgeType.SMOOTHSTEP}, edges)]);
    },
  });
}

function editConnection(
  edge: Edge,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  conjugateConnectionMap: ConjugateConnectionMap,
  setConjugateConnectionMap: (map: ConjugateConnectionMap) => void,
  canvasProps: ComplexPolymerDragAndDropCanvasProps,
  nodes: Array<StructureGraphNode>,
  edges: Array<Edge>,
  setEdges: (edges: Array<Edge>) => void,
  updateConnectionState: (
    conjugateConnection: ConjugateConnection,
    attachmentMolfile: string | null,
    isEditMode: boolean
  ) => void
): void {
  const {complexPolymerType, complexPolymerId, simplePolymers} = canvasProps;

  const conjugateConnectionMatches = Object.entries(conjugateConnectionMap).filter(
    ([_, conjugateConnection]) => doesEdgeMatchConjugateConnection(edge, nodes, conjugateConnection)
  );

  assert(conjugateConnectionMatches.length === 1, 'Expected exactly one matching conjugate connection');
  const [_, conjugateConnection] = conjugateConnectionMatches[0];

  legacyRunModal<ComplexPolymerAttachConjugateModalProps>(ComplexPolymerAttachConjugateModal, {
    isEditMode: true,
    complexPolymerType,
    complexPolymerId,
    startingConjugateRef: conjugateConnection.helmReference,
    simplePolymers,
    resolvedAttachments: resolvedAttachmentInputs,
    conjugateConnectionMap,
    onRequestClose: () => null,
    onAddConnection: (newConjugateConnection: ConjugateConnection, attachmentMolfile: string | null) => {
      const newConjugateConnectionMap = deepCopy(conjugateConnectionMap);
      newConjugateConnectionMap[newConjugateConnection.helmReference] = newConjugateConnection;
      setConjugateConnectionMap(newConjugateConnectionMap);
      updateConnectionState(newConjugateConnection, attachmentMolfile, true);
      setEdges([...addEdge({...edge, type: EdgeType.SMOOTHSTEP}, edges)]);
    },
  });
}

function clearConnectionFromConjugateConnectionMap(
  connectionToDelete: ComplexPolymerConnection,
  conjugateConnectionMap: ConjugateConnectionMap
): ConjugateConnectionMap {
  const conjugateConnectionMatches = Object.entries(conjugateConnectionMap).filter(
    ([_, conjugateConnection]) =>
      doesConnectionMatchConjugateConnection(
        connectionToDelete.source.polymer_ref,
        connectionToDelete.target.polymer_ref,
        conjugateConnection
      )
  );

  assert(conjugateConnectionMatches.length === 1, 'Expected exactly one matching conjugate connection');
  const [conjugateConnectionKey, conjugateConnection] = conjugateConnectionMatches[0];

  conjugateConnectionMap[conjugateConnectionKey] = {
    ...conjugateConnection,
    residueIndex: null,
    modificationType: null,
    rGroup: null,
    linkedEntityHelmReference: undefined,
    linkedEntityResidueIndex: undefined,
    linkedEntityModificationType: null,
    linkedEntityRGroup: null,
    attachmentMolfile: null,
  };
  return conjugateConnectionMap;
}

function deleteConnection(
  edge: Edge,
  simplePolymers: Array<SimplePolymerGraphQL>,
  connectionInputs: Array<ComplexPolymerConnection>,
  setConnectionInputs: (connections: Array<ComplexPolymerConnection>) => void,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  setResolvedAttachmentInputs: (attachments: Array<ResolvedAttachmentInput>) => void,
  conjugateConnectionMap: ConjugateConnectionMap,
  setConjugateConnectionMap: (map: ConjugateConnectionMap) => void,
  nodes: Array<StructureGraphNode>,
  edges: Array<Edge>,
  setEdges: (edges: Array<Edge>) => void
): void {
  const connectionToDelete = connectionInputs.find((connection) =>
    doesEdgeMatchComplexPolymerConnection(edge, nodes, connection)
  )!;

  const attachmentStyle = connectionToDelete.attachment_style ?? ComplexPolymerAttachmentStyle.LEAVING_GROUP;

  if (attachmentStyle === ComplexPolymerAttachmentStyle.LEAVING_GROUP) {
    const newConnectionInputs = connectionInputs.filter(
      (connection) => connectionHash(connection) !== connectionHash(connectionToDelete)
    );

    // This will automatically filter out any resolved attachments containing leaving group-style connections.
    // Resolved attachments are then automatically generated on submission for any leaving group-style connections,
    // so filtering out the connection to delete above is sufficient to update the resolved attachments.
    const newResolvedAttachmentInputs = getResolvedAttachmentsInputsForSubmit(
      false,
      resolvedAttachmentInputs,
      connectionToDelete,
      null
    );

    let newConjugateConnectionMap: ConjugateConnectionMap = deepCopy(conjugateConnectionMap);
    newConjugateConnectionMap = clearConnectionFromConjugateConnectionMap(
      connectionToDelete,
      newConjugateConnectionMap
    );

    const newEdges = edges.filter((oldEdge) => oldEdge.id !== edge.id);

    setConnectionInputs(newConnectionInputs);
    setResolvedAttachmentInputs(newResolvedAttachmentInputs);
    setConjugateConnectionMap(newConjugateConnectionMap);
    setEdges(newEdges);
  } else {
    const resolvedAttachmentToDelete = resolvedAttachmentInputs.find((resolvedAttachment) =>
      resolvedAttachment.connections_involved.some(
        (connectionInvolved) => connectionHash(connectionInvolved) === connectionHash(connectionToDelete)
      )
    )!;
    const newResolvedAttachmentInputs = resolvedAttachmentInputs.filter(
      (attachment) =>
        resolvedAttachmentHash(attachment) !== resolvedAttachmentHash(resolvedAttachmentToDelete)
    );

    const allConnectionsToDelete = resolvedAttachmentToDelete.connections_involved;
    const allConnectionsToDeleteHashes = new Set(
      allConnectionsToDelete.map((connection) => connectionHash(connection))
    );
    const newConnectionInputs = connectionInputs.filter(
      (connection) => !allConnectionsToDeleteHashes.has(connectionHash(connection))
    );

    let newConjugateConnectionMap: ConjugateConnectionMap = deepCopy(conjugateConnectionMap);
    allConnectionsToDelete.forEach((connectionToDelete) => {
      newConjugateConnectionMap = clearConnectionFromConjugateConnectionMap(
        connectionToDelete,
        newConjugateConnectionMap
      );
    });

    const newEdges = edges.filter(
      (edge) =>
        allConnectionsToDelete.find((conn) => doesEdgeMatchComplexPolymerConnection(edge, nodes, conn)) ==
        null
    );

    legacyRunModal<ComplexPolymerDragAndDropRemoveAttachmentWarningModalProps>(
      ComplexPolymerDragAndDropRemoveAttachmentWarningModal,
      {
        resolvedAttachment: resolvedAttachmentToDelete,
        simplePolymers,
        onRequestSubmit: () => {
          setConnectionInputs(newConnectionInputs);
          setResolvedAttachmentInputs(newResolvedAttachmentInputs);
          setConjugateConnectionMap(newConjugateConnectionMap);
          setEdges(newEdges);
        },
      }
    );
  }
}

export interface ComplexPolymerDragAndDropCanvasProps {
  isEditable: boolean;
  complexPolymerType: ComplexPolymerType;
  complexPolymerId: string;
  versionId: string;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  conjugates: Array<ConjugateGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  forwardOffset: number | null;
  structureGraph: StructureGraphGraphQL | null;
  outOfSync: boolean;
  outOfSyncReason: string | null;
}

export default function ComplexPolymerDragAndDropCanvas(
  props: ComplexPolymerDragAndDropCanvasProps
): JSX.Element {
  const {
    isEditable,
    complexPolymerType,
    complexPolymerId,
    versionId,
    simplePolymers,
    connections,
    conjugates,
    resolvedAttachments,
    forwardOffset,
    structureGraph,
    outOfSync,
    outOfSyncReason,
  } = props;

  const dispatch = useDispatch<BenchlingDispatch>();

  const isEditing = useSelector(
    (state: BenchlingState) => state.ui.modbio.complexPolymerVersionUIs[complexPolymerId].isEditing
  );
  const [connectionInputs, setConnectionInputs] = useState<Array<ComplexPolymerConnection>>(
    getConnectionInputs(connections)
  );
  const [resolvedAttachmentInputs, setResolvedAttachmentInputs] = useState<Array<ResolvedAttachmentInput>>(
    resolvedAttachments.map((resolvedAttachment) => getResolvedAttachmentInput(resolvedAttachment))
  );
  const [conjugateConnectionMap, setConjugateConnectionMap] = useState(
    makeConjugateConnections(conjugates, simplePolymers, connections, complexPolymerType)
  );

  const nodeTypes = useMemo(
    () => ({
      [NodeType.NUCLEOTIDE_NODE]: CustomNucleotideNode,
      [NodeType.CONJUGATE_NODE]: CustomConjugateChipNode,
    }),
    []
  );

  let initialNodes = [];
  let initialEdges = [];

  if (structureGraph == null) {
    initialNodes = generatePolymerStructureGraphNodes(complexPolymerType, simplePolymers, forwardOffset);
    initialEdges = generatePolymerStructureGraphEdges(complexPolymerType, initialNodes, resolvedAttachments);
    const layoutedGraph = layoutPolymerStructureGraph(
      complexPolymerType,
      initialNodes,
      initialEdges,
      resolvedAttachments
    );
    initialNodes = layoutedGraph.nodes;
    initialEdges = layoutedGraph.edges;
  } else {
    initialNodes = transformStructureGraphNodeForCanvas(complexPolymerType, structureGraph.nodes);
    initialEdges = transformStructureGraphEdgeForCanvas(structureGraph.edges);
  }

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);

  const setIsEditingAndSubmit = useCallback(
    async (newIsEditing: boolean): Promise<void> => {
      if (isEditing === true && newIsEditing === false) {
        const complexPolymerDisplayName = COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType];
        LoadingScreen.show(`Updating ${complexPolymerDisplayName}...`);

        const structureGraphForSubmit = {nodes, edges};
        const entityIds = simplePolymers.map((polymer) => {
          assert(!isInaccessible(polymer.entity), 'Expected accessible polymer on edit');
          return polymer.entity.id;
        });
        const connectionInputsForSubmit = connectionInputs.filter(
          (connection) => connection.connection_type !== ComplexPolymerConnectionType.HYDROGEN_BOND
        );
        const resolvedAttachmentInputsForSubmit = resolvedAttachmentInputs.filter((resolvedAttachment) =>
          resolvedAttachment.connections_involved.every(
            (c) => c.attachment_style === ComplexPolymerAttachmentStyle.DRAWN
          )
        );
        const entityAssociations = generateEntityAssociationsFromStructure(simplePolymers);

        try {
          await ComplexPolymerActions.updateComplexPolymer(
            complexPolymerId,
            versionId,
            entityIds,
            connectionInputsForSubmit,
            resolvedAttachmentInputsForSubmit,
            entityAssociations,
            complexPolymerType,
            forwardOffset ?? undefined,
            structureGraphForSubmit
          );
          dispatch(updateComplexPolymerEditingState(props.complexPolymerId, newIsEditing));
        } finally {
          LoadingScreen.hide();
        }
      } else {
        dispatch(updateComplexPolymerEditingState(props.complexPolymerId, newIsEditing));
      }
    },
    [
      isEditing,
      nodes,
      edges,
      simplePolymers,
      connectionInputs,
      resolvedAttachmentInputs,
      complexPolymerId,
      versionId,
      complexPolymerType,
      forwardOffset,
      dispatch,
      props.complexPolymerId,
    ]
  );

  const handleNodesChange = useCallback(
    (changes: Array<NodeChange>): void => {
      const dimensionsChanges = changes.filter((c): c is NodeDimensionChange => c.type === 'dimensions');
      let updatedNodes = nodes;
      dimensionsChanges.forEach((change) => {
        updatedNodes = handleNodeDimensionChange(updatedNodes, change);
      });

      if (structureGraph == null && !isEditing) {
        const {nodes: layoutedNodes, edges: layoutedEdges} = layoutPolymerStructureGraph(
          complexPolymerType,
          updatedNodes,
          edges,
          resolvedAttachments
        );
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
      }

      if (isEditing) {
        onNodesChange(changes.filter((c) => c.type !== 'remove'));
      }
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      complexPolymerType,
      resolvedAttachments,
      structureGraph,
      onNodesChange,
      isEditing,
    ]
  );

  const updateConnectionState = useCallback(
    (newConjugateConnection: ConjugateConnection, attachmentMolfile: string | null, isEditMode: boolean) => {
      const newConnectionInputs = getExistingConnectionInputs(
        newConjugateConnection.id,
        conjugateConnectionMap
      );

      // Add new connection to the existing connections
      assert(isCompleteConjugateConnection(newConjugateConnection), 'Expected connection to be connected.');
      const newConnection = getComplexPolymerConnectionFromConjugateConnection(newConjugateConnection);
      newConnectionInputs.push(newConnection);

      const newResolvedAttachmentInputs = getResolvedAttachmentsInputsForSubmit(
        isEditMode,
        resolvedAttachmentInputs,
        newConnection,
        attachmentMolfile
      );

      setConnectionInputs(newConnectionInputs);
      setResolvedAttachmentInputs(newResolvedAttachmentInputs);
    },
    [conjugateConnectionMap, setConnectionInputs, resolvedAttachmentInputs, setResolvedAttachmentInputs]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      addConnection(
        connection,
        resolvedAttachmentInputs,
        conjugateConnectionMap,
        setConjugateConnectionMap,
        props,
        nodes,
        edges,
        setEdges,
        updateConnectionState
      );
    },
    [
      nodes,
      edges,
      setEdges,
      resolvedAttachmentInputs,
      conjugateConnectionMap,
      setConjugateConnectionMap,
      props,
      updateConnectionState,
    ]
  );

  const onEdgeDoubleClick = useCallback(
    (_: MouseEvent, edge: Edge) => {
      if (!isEditing) {
        return;
      }

      editConnection(
        edge,
        resolvedAttachmentInputs,
        conjugateConnectionMap,
        setConjugateConnectionMap,
        props,
        nodes,
        edges,
        setEdges,
        updateConnectionState
      );
    },
    [
      isEditing,
      resolvedAttachmentInputs,
      conjugateConnectionMap,
      setConjugateConnectionMap,
      props,
      nodes,
      edges,
      setEdges,
      updateConnectionState,
    ]
  );

  const onEdgesDelete = useCallback(
    (edgesToDelete: Array<Edge>) => {
      if (!isEditing) {
        return;
      }

      deleteConnection(
        // We do not have multi-select enabled, so only a single edge can be selected at a time.
        edgesToDelete[0],
        simplePolymers,
        connectionInputs,
        setConnectionInputs,
        resolvedAttachmentInputs,
        setResolvedAttachmentInputs,
        conjugateConnectionMap,
        setConjugateConnectionMap,
        nodes,
        edges,
        setEdges
      );
    },
    [
      isEditing,
      simplePolymers,
      connectionInputs,
      setConnectionInputs,
      resolvedAttachmentInputs,
      setResolvedAttachmentInputs,
      conjugateConnectionMap,
      setConjugateConnectionMap,
      nodes,
      edges,
      setEdges,
    ]
  );

  const contextValue = useMemo(
    () => ({isEditing, setIsEditing: setIsEditingAndSubmit, edges}),
    [isEditing, setIsEditingAndSubmit, edges]
  );

  const axises = getNucleotideAxisDatas(complexPolymerType, simplePolymers, forwardOffset ?? 0);
  const backbonePolymers = simplePolymers.filter((polymer) =>
    isPolymerBackboneStrand(complexPolymerType, polymer.helmReference)
  );

  return (
    <ComplexPolymerDragAndDropCanvasContext.Provider value={contextValue}>
      <ReactFlowProvider>
        <ReactFlow
          style={{background: isEditing ? '#EAEFF6' : '#edeef0'}}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          onNodesChange={handleNodesChange}
          onConnect={onConnect}
          onEdgeDoubleClick={onEdgeDoubleClick}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={() => {
            notifications.warn('Component entities must be deleted from the Polymer Components tab');
          }}
          elementsSelectable={isEditing}
          multiSelectionKeyCode={null}
          zoomOnDoubleClick={false}
          fitView={true}
          proOptions={{hideAttribution: true}}
        >
          <Background color="#d9dadc" size={2.5} variant={BackgroundVariant.Dots} />
          {outOfSync && (
            <WarningBanner
              className={css(styles.warningBanner)}
              message={`${capitalize(
                COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType]
              )} is out of sync with its component entities. ${outOfSyncReason}`}
            />
          )}
          {axises.map((axis) => (
            <NucleotideAxis key={axis.id} complexPolymerType={complexPolymerType} {...axis} />
          ))}
          <div className={css(styles.legend)}>
            <ModifiedNucleotidesLegend
              complexPolymerType={complexPolymerType}
              backbonePolymers={backbonePolymers}
            />
          </div>
          <div className={css(styles.editToggle)}>
            <EditToggleButton isEditable={isEditable} />
          </div>
          <Controls position="bottom-right" showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </ComplexPolymerDragAndDropCanvasContext.Provider>
  );
}

const styles = StyleSheet.create({
  legend: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    maxWidth: 300,
    zIndex: 5,
  },
  editToggle: {
    position: 'absolute',
    bottom: 20,
    right: 50,
    zIndex: 5,
  },
  warningBanner: {
    position: 'absolute',
    top: 10,
    zIndex: 5,
    margin: 10,
  },
});
